import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  ANALYZER_ACTION,
  ANALYZER_ENTITY,
  ANALYZER_TIME_DELAY,
  CONTENT_KEY,
} from '../../commons/constants';
import { RootState } from '../../redux/store';
import { getSelectedFloor } from '../../redux/selectors/floor.selector';
import { getProject } from '../../redux/selectors/project.selector';
import { getChildItemById } from '../../redux/selectors/projectChildItem.selector';
import {
  getFocusProjectItem,
  getSelectedProjectItem,
} from '../../redux/selectors/projectItem.selector';
import useBeforeunload from '../useBeforeunload';
import { getFloorName } from '../../utils';
import { FocusItemTypes } from '../../types';

type UseAnalyzerSitePlanPropsTypes = {
  initial: boolean;
};

const useAnalyzerSitePlan = (props: UseAnalyzerSitePlanPropsTypes): void => {
  const { initial } = props;

  const [buildingCode, setBuildingCode] = useState<string>();
  const [buildingName, setBuildingName] = useState<string>();
  const [floorCode, setFloorCode] = useState<string>();
  const [floorName, setFloorName] = useState<string>();
  const [floorNumber, setFloorNumber] = useState<string>();
  const [apartmentCode, setApartmentCode] = useState<string>();
  const [apartmentName, setApartmentName] = useState<string>();
  const [prevFocusProjectItem, setPrevFocusProjectItem] =
    useState<{ code: string; name: string }>();

  const contentActiveKey = useSelector((state: RootState) => state.projectModel.contentActiveKey);
  const isActivityWindow = useSelector((state: RootState) => state.layoutModel.isActivityWindow);
  const projectItemId = useSelector((state: RootState) => state.sitePlanModel.projectItemId);
  const floorId = useSelector((state: RootState) => state.sitePlanModel.floorId);
  const projectChildItemId = useSelector(
    (state: RootState) => state.sitePlanModel.projectChildItemId
  );
  const focusProjectItemFlag: FocusItemTypes = useSelector(
    (state: RootState) => state.sitePlanModel.focusProjectItem
  );
  const project = useSelector(getProject);
  const projectItem = useSelector(getSelectedProjectItem);
  const focusProjectItem = useSelector(getFocusProjectItem);
  const floor = useSelector(getSelectedFloor);
  const apartment = useSelector(getChildItemById);

  const buildingAnalyzer = (action: string, code: string, name: string) => {
    if (!project) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.BUILDING, {
        project_code: project.code,
        project_name: project.name,
        building_code: code,
        building_name: name,
      });
    }
  };

  const overviewSiteplanAnalyzer = (action: string, code: string, name: string) => {
    if (!project) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.OVERVIEW_SITEPLAN, {
        project_code: project.code,
        project_name: project.name,
        building_code: code,
        building_name: name,
      });
    }
  };

  const floorAnalyzer = (action: string, code: string, name: string, number: string) => {
    if (!project) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.FLOOR_PLAN, {
        project_code: project.code,
        project_name: project.name,
        building_code: buildingCode,
        building_name: buildingName,
        floor_code: code,
        floor_name: name,
        floor_number: number,
      });
    }
  };

  const apartmentAnalyzer = (action: string, code: string, name: string) => {
    if (!project) return;

    if (window && window.ClikTracker && window.ClikTracker.sendCustomTrack) {
      window.ClikTracker.sendCustomTrack(action, ANALYZER_ENTITY.APARTMENT, {
        project_code: project.code,
        project_name: project.name,
        building_code: buildingCode,
        building_name: buildingName,
        floor_code: floorCode,
        floor_name: floorName,
        floor_number: floorNumber,
        apartment_code: code,
        apartment_name: name,
      });
    }
  };

  // run event when close tab window or reload page
  useBeforeunload(async (event) => {
    if (!initial) return;
    const promiseAll = await Promise.all([
      new Promise((resolve) => {
        if (buildingCode) {
          buildingAnalyzer(ANALYZER_ACTION.CLOSED, buildingCode, buildingName);
        }

        resolve(true);
      }),
      new Promise((resolve) => {
        if (floorCode) {
          floorAnalyzer(ANALYZER_ACTION.CLOSED, floorCode, floorName, floorNumber);
        }

        resolve(true);
      }),
      new Promise((resolve) => {
        if (apartmentCode) {
          apartmentAnalyzer(ANALYZER_ACTION.CLOSED, apartmentCode, apartmentName);
        }

        resolve(true);
      }),
    ]);

    // delay close tab for waiting send data to tracking module
    const start = Date.now();
    let now = start;
    while (now - start < ANALYZER_TIME_DELAY) {
      if (promiseAll.every((i) => i === true)) now = Date.now();
    }

    // this is needed to avoid to show a confirmation prompt
    delete event['returnValue'];
  });

  // useEffect building
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.SITEPLAN.key) return;

    // case close building
    if (
      (buildingCode && !projectItem) ||
      (buildingCode && projectItem && buildingCode !== projectItem.code)
    ) {
      buildingAnalyzer(ANALYZER_ACTION.CLOSED, buildingCode, buildingName);

      if (!projectItem) {
        setBuildingName(undefined);
        setBuildingCode(undefined);
      }
    }

    // case view building
    if (
      (!buildingCode && projectItem) ||
      (buildingCode && projectItem && buildingCode !== projectItem.code)
    ) {
      setBuildingName(projectItem.name);
      setBuildingCode(projectItem.code);
      buildingAnalyzer(ANALYZER_ACTION.VIEWED, projectItem.code, projectItem.name);
    }
  }, [initial, contentActiveKey, projectItemId]);

  // useEffect floor
  useEffect(() => {
    if (!initial || contentActiveKey !== CONTENT_KEY.SITEPLAN.key) return;

    // case close floor
    if ((floorCode && !floor) || (floor && floorCode && floorCode !== floor.code)) {
      floorAnalyzer(ANALYZER_ACTION.CLOSED, floorCode, floorName, floorNumber);
      if (!floor) {
        setFloorName(undefined);
        setFloorNumber(undefined);
        setFloorCode(undefined);
      }
    }

    // case view floor and change new floor
    if ((!floorCode && floor) || (floor && floorCode && floorCode !== floor.code)) {
      const name = getFloorName(floor.name);
      setFloorName(name);
      setFloorNumber(floor.floorNumber);
      setFloorCode(floor.code);
      floorAnalyzer(ANALYZER_ACTION.VIEWED, floor.code, name, floor.floorNumber);
    }
  }, [initial, contentActiveKey, floorId]);

  // useEffect apartment
  useEffect(() => {
    // check content page
    if (!initial || contentActiveKey !== CONTENT_KEY.SITEPLAN.key) return;

    // case close apartment
    if (
      (apartmentCode && !projectChildItemId) ||
      (apartmentCode && apartment && apartmentCode !== apartment.code)
    ) {
      apartmentAnalyzer(ANALYZER_ACTION.CLOSED, apartmentCode, apartmentName);
      if (!apartment) {
        setApartmentName(undefined);
        setApartmentCode(undefined);
      }
    }

    // case view apartment and change new apartment
    if (
      (!apartmentCode && apartment) ||
      (apartmentCode && apartment && apartmentCode !== apartment.code)
    ) {
      setApartmentName(apartment.name);
      setApartmentCode(apartment.code);
      apartmentAnalyzer(ANALYZER_ACTION.VIEWED, apartment.code, apartment.name);
    }
  }, [initial, contentActiveKey, projectChildItemId]);

  useEffect(() => {
    if (!initial || contentActiveKey === CONTENT_KEY.SITEPLAN.key) return;

    if (apartmentCode) {
      apartmentAnalyzer(ANALYZER_ACTION.CLOSED, apartmentCode, apartmentName);
      setApartmentName(undefined);
      setApartmentCode(undefined);
    }

    if (floorCode) {
      floorAnalyzer(ANALYZER_ACTION.CLOSED, floorCode, floorName, floorNumber);
      setFloorName(undefined);
      setFloorNumber(undefined);
      setFloorCode(undefined);
    }

    if (buildingCode) {
      buildingAnalyzer(ANALYZER_ACTION.CLOSED, buildingCode, buildingName);
      setBuildingName(undefined);
      setBuildingCode(undefined);
    }
  }, [contentActiveKey]);

  useEffect(() => {
    if (!initial) return;

    let action = ANALYZER_ACTION.INTERRUPTED;
    if (isActivityWindow) action = ANALYZER_ACTION.RESUMED;

    if (apartmentCode) {
      apartmentAnalyzer(action, apartmentCode, apartmentName);
    }

    if (floorCode) {
      floorAnalyzer(action, floorCode, floorName, floorNumber);
    }

    if (buildingCode) {
      buildingAnalyzer(action, buildingCode, buildingName);
    }

    if (focusProjectItem) {
      overviewSiteplanAnalyzer(action, focusProjectItem.code, focusProjectItem.name);
    }
  }, [isActivityWindow]);

  useEffect(() => {
    if (!initial) return;

    if (!focusProjectItemFlag || !focusProjectItemFlag.isShowModal) {
      if (prevFocusProjectItem) {
        setPrevFocusProjectItem(null);
        overviewSiteplanAnalyzer(
          ANALYZER_ACTION.CLOSED,
          prevFocusProjectItem.code,
          prevFocusProjectItem.name
        );
      }
      return;
    }

    setPrevFocusProjectItem(focusProjectItem);
    if (focusProjectItem !== prevFocusProjectItem) {
      if (prevFocusProjectItem) {
        overviewSiteplanAnalyzer(
          ANALYZER_ACTION.CLOSED,
          prevFocusProjectItem.code,
          prevFocusProjectItem.name
        );
      }

      overviewSiteplanAnalyzer(
        ANALYZER_ACTION.VIEWED,
        focusProjectItem.code,
        focusProjectItem.name
      );
    }
  }, [focusProjectItemFlag]);
};

export default useAnalyzerSitePlan;
